import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
  DENTURE_TYPE_OPTIONS,
  DENTURE_AGE_OPTIONS,
  ORAL_PROBLEM_OPTIONS,
  LAST_DENTIST_VISIT_OPTIONS,
  initialFormData,
  YES_NO_CHOICES,
} from "./initialOralHealthAssessments.config";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import MultiSelect from "../../../../../FormFields/CustomSelect/MultiSelect";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import DynamicSelectInput from "../../../../../FormFields/CustomSelect/DynamicSelectInput";
import { createInitialOralHealthAssessment } from "../../../../../../api/services/assessment";
import { getCarePlans } from "../../../../../../api/services/core";
import { QK } from "../../../../../../base/qk";
import CustomSwitch from "../../../../../FormFields/CustomSwitch";

const CreateInitialOralHealthAssessmentModal = ({ open, handleClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();

  const { data: carePlans } = useQuery([QK.carePlans], () => getCarePlans());

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: initialFormData,
  });

  const watchHasDentures = useWatch({ control, name: "has_dentures" });
  const watchHasOralProblems = useWatch({ control, name: "has_oral_problems" });
  const watchRequiresUrgentCheckup = useWatch({
    control,
    name: "requires_urgent_checkup",
  });
  const watchRegisteredWithDentist = useWatch({
    control,
    name: "is_registered_with_dentist",
  });

  const createAssessmentMutation = useMutation(
    (assessmentData) => createInitialOralHealthAssessment(assessmentData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          QK.initialOralHealthAssessments,
          clientID,
        ]);
        enqueueSnackbar("Oral health assessment created successfully", {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to create assessment",
          { variant: "error" }
        );
      },
    }
  );

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        client: clientID,
        denture_types: data.denture_types.map((item) => item.value),
        oral_problems_list: data.oral_problems_list.map((item) => item.value),
      };
      await createAssessmentMutation.mutateAsync(payload);
      handleCloseModal();
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleCloseModal = () => {
    reset();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleCloseModal} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Create Initial Oral Health Assessment
          </Typography>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ p: 2 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Controller
                  name="care_plan"
                  control={control}
                  rules={{ required: "Care plan is required" }}
                  render={({ field }) => (
                    <DynamicSelectInput
                      options={carePlans}
                      label="Which care plan is this linked to? *"
                      {...field}
                      error={!!errors.care_plan}
                      helperText={errors.care_plan?.message}
                      required
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="has_dentures"
                  control={control}
                  render={({ field }) => (
                    <CustomSwitch
                      label="Does the individual have dentures?"
                      labelPlacement="start"
                      {...field}
                      checked={field.value}
                    />
                  )}
                />
              </Grid>
              {watchHasDentures && (
                <>
                  <Grid item xs={12}>
                    <Controller
                      name="denture_types"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          placeholder="Please specify denture types..."
                          options={DENTURE_TYPE_OPTIONS}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="are_dentures_labelled"
                      control={control}
                      render={({ field }) => (
                        <CustomSelect
                          label="Are the dentures labelled?"
                          options={YES_NO_CHOICES}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="denture_age"
                      control={control}
                      render={({ field }) => (
                        <CustomSelect
                          label="How old are the dentures?"
                          options={DENTURE_AGE_OPTIONS}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Controller
                  name="has_oral_problems"
                  control={control}
                  render={({ field }) => (
                    <CustomSwitch
                      label="Is the individual experiencing any problems with their mouth/teeth/dentures?"
                      labelPlacement="start"
                      {...field}
                      checked={field.value}
                    />
                  )}
                />
              </Grid>
              {watchHasOralProblems && (
                <>
                  <Grid item xs={12}>
                    <Controller
                      name="oral_problems_list"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          placeholder="Please pick which problems apply..."
                          options={ORAL_PROBLEM_OPTIONS}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="oral_problems_description"
                      control={control}
                      render={({ field }) => (
                        <CustomInput
                          label="Describe the problems (consider referral to dentist/GP)"
                          multiline
                          rows={3}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Controller
                  name="requires_urgent_checkup"
                  control={control}
                  render={({ field }) => (
                    <CustomSwitch
                      label="Does the individual require an urgent dental check up?"
                      {...field}
                      labelPlacement="start"
                      checked={field.value}
                    />
                  )}
                />
              </Grid>
              {watchRequiresUrgentCheckup && (
                <Grid item xs={12}>
                  <Controller
                    name="urgent_checkup_details"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        label="Please give details for urgent check up"
                        multiline
                        rows={3}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Controller
                  name="last_dentist_visit"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      label="When did the individual last see a dentist?"
                      options={LAST_DENTIST_VISIT_OPTIONS}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="is_registered_with_dentist"
                  control={control}
                  render={({ field }) => (
                    <CustomSwitch
                      label="Are they registered with a dentist?"
                      labelPlacement="start"
                      {...field}
                      checked={field.value}
                    />
                  )}
                />
              </Grid>
              {watchRegisteredWithDentist && (
                <Grid item xs={12}>
                  <Controller
                    name="action_to_be_taken"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        label="Action to be taken"
                        multiline
                        rows={3}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </form>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCloseModal} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
          disabled={createAssessmentMutation.isLoading}
        >
          {createAssessmentMutation.isLoading
            ? "Creating..."
            : "Create Assessment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateInitialOralHealthAssessmentModal;
